import { useAppSelector } from "@/app/appStore";
import { useTimelinePlayback } from "@/contexts/playback-context";
import { Schedule, insightsApi } from "@/shared/api";
import { findCurrentSchedule } from "@/shared/helpers/findCurrentSchedule";
import { useObserve } from "@legendapp/state/react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

export function useSchedules({
  queryKey,
}: {
  queryKey:
    | "planogramSchedules"
    | "zoneHistorySchedules"
    | "calibrationSchedules";
}) {
  const timelinePlayback = useTimelinePlayback();
  const planogramDateRange = useAppSelector(
    (state) => state.planogram.dateRange
  );

  const { data, isFetching } = insightsApi.endpoints[queryKey].useQuery({
    from_datetime: planogramDateRange.start?.toISOString(),
    to_datetime: planogramDateRange.end?.toISOString(),
    ordering: "-from_datetime",
  });

  const [activeSchedule, setActiveSchedule] = useState<Schedule | null>(null);
  const isMounted = useRef(false);

  /**
   * When currentTime is changed, we need to update activeSchedule
   */
  useObserve(timelinePlayback.currentTime, ({ value }) => {
    const currentTime = value;

    /**
     * Edge case: When currentTime and planogramDateRange is changed at the same time, this callback don't wait for data to be loaded.
     * In that case, `data` is old state and calling `findCurrentSchedule` might return null because new data is not loaded yet.
     * Hence, we can skip the logic when data is loading or not loaded yet.
     */
    if (!data || !currentTime || !isMounted.current || isFetching) return;

    try {
      const currentSchedule = findCurrentSchedule(data.results, currentTime);

      if (
        currentSchedule &&
        activeSchedule &&
        currentSchedule.id !== activeSchedule.id
      ) {
        setActiveSchedule(currentSchedule);
      }
    } catch (err) {
      toast.error((err as Error).message, {
        id: "schedule-error",
        duration: 1000 * 10,
      });
    }
  });

  /**
   * Update active schedule when data is loaded
   */
  useEffect(() => {
    if (!data) return;

    const currentTime = timelinePlayback.currentTime.peek() || Date.now();

    try {
      const currentSchedule = findCurrentSchedule(data.results, currentTime); // Passing now will return latest schedule

      if (currentSchedule && currentSchedule.id !== activeSchedule?.id) {
        isMounted.current = true;
        setActiveSchedule(currentSchedule);
      }
    } catch (err) {
      toast.error(`Failed to display schedule: ${(err as Error).message}`, {
        id: "schedule-error",
        duration: 1000 * 10,
      });
    }
  }, [data, activeSchedule]);

  return { activeSchedule };
}
